// Data types
// Dependencies
import { FlyToInterpolator, WebMercatorViewport } from 'deck.gl';
import React, { RefObject } from 'react';
import { connect } from 'react-redux';
import { GeoJSONFeature } from 'types/geojson';

// Components
import { Deck as DeckGl } from '@deck.gl/core';
import { GeoJsonLayer } from '@deck.gl/layers';
import BaseMap from 'components/baseClass/MapBase';
import { LAYERS, PLAYBACK_SPEED, TAB_IDEL_TIMEOUT, TOOLTIP_OFFSET, TRACK_UNUSED_TABS } from 'data/static';
import { bindActionCreators, Dispatch } from 'redux';
import {
    setErrorHandlerAction,
    SetErrorHandlerAction,
    setSystemLoadingAction,
    SetSystemLoadingAction,
    ShowMessageAction,
    showMessageAction,
} from 'store/actions/errorsActions';
import { SetLayoutAction, setLayoutAction } from 'store/actions/layoutActions';
import { setMapAction, SetMapAction, SetMapControlsAction, setMapControlsAction } from 'store/actions/mapActions';
import {
    BackgroundType,
    CoordinateArray,
    DeckGlInstance,
    DeckGLLayer,
    LayerType,
    Layout,
    MapProps,
    OpenModal,
    PilotagePlanSettingType,
    PlanType,
    PortType,
    RGBAColor,
    RootState,
    SwitchType,
    ToolTipType,
    TrackType,
    VesselCategoryType,
} from 'types';
import { GoToVesselLocation, JumpToTimeInterface, MainControlsInterface, MenuControlsInterface } from 'types/functions';
import { GeoJSONFeatureCollection } from 'types/geojson';
import { hexToRgb } from 'utils/colors';
import { DateTimeFormat } from 'utils/dateTimeFormat';
import { getVesselDisplayFeature } from 'utils/drawing';
import { checkDifferent, checkLayoutDifferent, clone, getId } from 'utils/formHandlers';
import geometry from 'utils/geometry';
import {
    filterBySection,
    getLayerProvider,
    getPortCoordinate,
    getScreenCoordinates,
    updateFeatureProperties,
} from 'utils/layers';
import { findBestData, findBestPosition } from 'utils/migrations';
import { translate } from 'utils/translate';
import { twclsx } from 'utils/twclsx';

import Attribution from './Attribution';
import ChangeLayer from './ChangeLayer';
import Controls from './Controls';
import MeasurePosition from './MeasurePosition';
import MousePosition from './MousePosition';
import TimeControl from './TimeControl';
import ToolTip from './ToolTip';
import updatePageTitle from 'utils/document';

interface TimeComponentProps {
    active: boolean;
    port: PortType;
    layout: Layout;
    //unused props
    outlineInterval: number;

    switchs: SwitchType;
    layers: LayerType;
    setMapControlsAction: SetMapControlsAction;
    setMapAction: SetMapAction;
    openModelFunction: OpenModal;
    setLayoutAction: SetLayoutAction;
    vesselCategories: VesselCategoryType[];
    //unused props
    setErrorHandlerAction: SetErrorHandlerAction;

    //unused props
    setSystemLoadingAction: SetSystemLoadingAction;

    showMessageAction: ShowMessageAction;
    pilotagePlans: PlanType[];
    pilotagePlanSettings: PilotagePlanSettingType;

    //unused props
    dateTimeFormat: DateTimeFormat;

    //unused props
    mainControls: MainControlsInterface;

    //unused props
    menuControls: MenuControlsInterface;
}

interface TimeComponentState {
    uuid: string;
    active: boolean;
    zoom: number;
    layout: Layout;
    mode: string;
    coordinate: CoordinateArray | null;
    hoverMeasureData: any | null;
    backgrounds: BackgroundType[];
    mapProps?: MapProps;
    switchs: SwitchType;
    map?: DeckGlInstance;
    tooltips: ToolTipType[];
    drawingFeatures: GeoJSONFeatureCollection;
    pilotagePlans: PlanType[];
    pilotagePlanSettings: PilotagePlanSettingType;
    refreshFeatures: string;
    layers: LayerType;
    outlineFeatures: TrackType[];
    trackFeatures: TrackType[];
    currentTime?: DateTimeFormat;
    startTime?: DateTimeFormat;
    endTime?: DateTimeFormat;
    speed: number;
    play: boolean;
    clickMatrix: any[];
    //unused state
    drawingMode: string;
    analysisVariables: object;
}

class Time extends BaseMap<TimeComponentProps, TimeComponentState> {
    private canvasRef: RefObject<HTMLCanvasElement>;
    private timer: ReturnType<typeof setInterval> | undefined = undefined;
    private timeoutId: NodeJS.Timeout | null = null;

    constructor(props: TimeComponentProps) {
        super(props);
        this.state = {
            layout: clone(props.layout),
            drawingMode: 'pointer',
            uuid: '',
            mode: 'time',
            active: true,
            zoom: 10,
            coordinate: null,
            drawingFeatures: {
                type: 'FeatureCollection',
                features: [],
            },
            hoverMeasureData: null,
            backgrounds: [],
            tooltips: [],
            switchs: clone(props.switchs),
            pilotagePlans: [],
            pilotagePlanSettings: {},
            refreshFeatures: getId(),
            layers: props.layers,
            outlineFeatures: [],
            trackFeatures: [],
            speed: PLAYBACK_SPEED,
            play: false,
            clickMatrix: [],
            analysisVariables: props.port.meta_data.analysisVariables,
        };
        // Initialize the ref in the constructor
        this.canvasRef = React.createRef();
        //@ts-ignore
        this.geometry = new geometry(props.port?.projection, 'EPSG:4326');
    }

    static getDerivedStateFromProps(props: TimeComponentProps, state: TimeComponentState) {
        //VERY important code to keep stop COMM update if not active

        if (!props.layout.sections?.time.state) {
            if (props.active) {
                updatePageTitle(
                    `${translate('Replay')} AIS ${state.currentTime?.formatDateTimeToString('dd MMM  HH:mm') || ''} · ${
                        props.port.name
                    }`
                );
            }
            return {
                active: props.active,
                layout: clone(props.layout),
                pilotagePlans: props.pilotagePlans,
                pilotagePlanSettings: props.pilotagePlanSettings,
            };
        }

        if (
            props.active !== state.active ||
            checkDifferent(props.switchs, state.switchs, true) ||
            checkDifferent(props.layers, state.layers) ||
            checkLayoutDifferent(props.layout, state.layout, ['sections.time']) ||
            checkDifferent(props.pilotagePlanSettings, state.pilotagePlanSettings, true) ||
            checkDifferent(props.pilotagePlans, state.pilotagePlans, true)
        ) {
            if (props.active) {
                updatePageTitle(
                    `${translate('Replay')} AIS ${state.currentTime?.formatDateTimeToString('dd MMM  HH:mm') || ''} · ${
                        props.port.name
                    }`
                );
            }
            return {
                active: props.active,
                layout: clone(props.layout),
                switchs: clone(props.switchs),
                layers: clone(props.layers),
                pilotagePlans: props.pilotagePlans,
                pilotagePlanSettings: props.pilotagePlanSettings,
            };
        }

        return null;
    }

    componentDidMount() {
        const { latitude, longitude, zoom } = getPortCoordinate(this.props.port);
        this.setState(
            { uuid: getId(), zoom, coordinate: [latitude, longitude], backgrounds: this.getPortBackgrounds('time') },
            this.createMap
        );
    }

    componentWillUnmount(): void {
        this.props.setMapControlsAction({
            time: null,
        });
        this.props.setMapAction({
            time: null,
        });
    }

    updateVesselsOulines = () => {
        console.log('not used');
    };

    closeTimeMode = () => {
        this.props.setLayoutAction([
            { action: 'UPDATE_SECTION', data: { name: 'replay', state: false, display: true, disable: true } },
            { action: 'UPDATE_SECTION', data: { name: 'time', state: false, display: true, disable: true } },
            {
                action: 'UPDATE_MENU',
                data: {
                    name: 'timeMenu',
                    section: 'time',
                    state: false,
                },
            },
            {
                action: 'UPDATE_DATA',
                data: {
                    section: 'time',
                    data: {},
                },
            },
        ]);
    };

    async componentDidUpdate(
        props: Readonly<TimeComponentProps>,
        state: Readonly<TimeComponentState>
    ): Promise<Promise<void>> {
        const previousZoom = state.mapProps?.viewState?.zoom;
        const currentZoom = this.state.mapProps?.viewState?.zoom;

        if (previousZoom !== currentZoom) {
            this.renderOutlineAndTracks();
        }


        // Check if state.switches has changed
        if (state.active !== this.state.active && TRACK_UNUSED_TABS) {
            if (this.state.active) {
                //@ts-ignore
                clearTimeout(this.timeoutId);
                this.timeoutId = null;
            } else {
                this.timeoutId = setTimeout(() => {
                    this.props.openModelFunction(
                        'timeModeTimeOutModel',
                        0,
                        'medium',
                        {},
                        {
                            onCancel: () => {
                                this.props.openModelFunction('timeModeTimeOutModel');
                            },
                            onConfirm: () => this.closeTimeMode(),
                        },
                        true
                    );
                    this.timeoutId = null;
                }, TAB_IDEL_TIMEOUT);
            }
        }

        if (
            Object.values(this.state.layout.sections.time.tools).every((tool) => tool.selected === false) &&
            this.state.drawingMode !== 'pointer'
        ) {
            this.setDrawingMode('pointer');
        }
        if (
            checkDifferent(
                filterBySection(state.switchs, 'analysisAreas'),
                filterBySection(this.state.switchs, 'analysisAreas'),
                true
            ) &&
            this.state.map
        ) {
            this.updatePortAreas();
        }

        if (checkDifferent(state.drawingFeatures, this.state.drawingFeatures, true) && this.state.map) {
            await this.createMeasureTooltip();
            await this.createLabelTooltip();
            await this.createCoordinateTooltip();
            this.createEditlayer();
        }

        // Check if pilotagePlanSettings or pilotagePlanSettings has changed
        if (
            checkDifferent(state.pilotagePlanSettings, this.state.pilotagePlanSettings, true) ||
            (checkDifferent(state.pilotagePlans, this.state.pilotagePlans, true) && this.state.map)
        ) {
            this.updatePilotagePlans(state.pilotagePlans);
        }
    }

    startTimer = () => {
        if (this.state.play) {
            window?.log('INFO : Aready playing');
            return;
        }

        this.setState(
            {
                play: true,
            },
            () => {
                window?.log('INFO : Start play');

                this.timer = setInterval(() => {
                    if (this.state.currentTime?.isAfter(this.state.endTime?.date)) {
                        this.props.showMessageAction({
                            messageType: 'INFO',
                            message: translate('Play Time completed.'),
                        });

                        this.stopTimer();
                        return;
                    }
                    this.setState(
                        (prevState) => ({
                            currentTime: prevState.currentTime?.clone().plus({ second: 1 * this.state.speed }), // Increment time
                        }),
                        () => {
                            this.renderOutlineAndTracks();
                            //@ts-ignore
                            this.props.menuControls?.time?.setSelectedTime(this.state.currentTime);
                        }
                    );
                }, 1000);
            }
        );
    };

    stopTimer = () => {
        if (!this.state.play) {
            window?.log('INFO : Aready stoped');
            return;
        }

        this.setState(
            {
                play: false,
            },
            () => {
                window?.log('INFO : Stop play');
                clearInterval(this.timer);
                this.timer = undefined;
            }
        );
    };

    updateTracks = async (
        tracks: TrackType[],
        selectedTime: DateTimeFormat | null,
        startTime: DateTimeFormat,
        endTime: DateTimeFormat
    ) => {
        window?.log('INFO : Map feature replace requested');

        // Update state and wait until it's done before proceeding
        await new Promise<void>((resolve) =>
            this.setState(
                {
                    currentTime: selectedTime ? selectedTime : this.state.currentTime,
                    startTime: startTime,
                    endTime: endTime,
                    outlineFeatures: tracks.filter((track) => track.options.display),
                    trackFeatures: tracks.filter((track) => track.options.drawTrack),
                },
                async () => {
                    try {
                        await this.renderOutlineAndTracks();
                        // await this.renderTracks();
                        return resolve(); // or any data you want to return
                    } catch (error) {
                        console.error('Error updating features:', error);
                        return resolve();
                    }
                }
            )
        );
    };

    goToVesselLocation: GoToVesselLocation = (uuid: string) => {
        const { outlineFeatures, currentTime, map, tooltips } = this.state;

        outlineFeatures
            .filter((x) => x.uuid === uuid)
            .forEach((outlineFeature) => {
                let position = outlineFeature.data.rawAIS.find((item, _, array) => {
                    //@ts-ignore
                    return item.valid_time_utc.toTimestamp() >= currentTime?.toTimestamp();
                });
                if (!position) {
                    position = outlineFeature.data.rawAIS[outlineFeature.data.rawAIS.length - 1];
                }

                // Transform UTM coordinates to map coordinates
                const [longitude, latitude] = this.geometry.transformer([position.east_utm, position.north_utm]);
                //@ts-ignore
                const currentViewState = map?.props?.viewState || {}; // Fallback to an empty object if undefined
                const { x, y } = getScreenCoordinates(longitude, latitude, currentViewState);
                // Retrieve current viewState from the map

                const tooltip = {
                    static: false,
                    uuid: getId(),
                    type: LAYERS.timeVesselOutline.name,
                    event: 'onClick',
                    bind: false,
                    longitude: longitude,
                    latitude: latitude,
                    east_utm: position.east_utm,
                    north_utm: position.north_utm,
                    x: x + TOOLTIP_OFFSET,
                    y: y + TOOLTIP_OFFSET,
                    xOffset: 0,
                    yOffset: 0,
                    dataIndex: {
                        type: 'rawAIS',
                        index: null,
                        navigational_status_id: position.navigational_status_id,
                        time: position?.valid_time_utc,
                    },
                    data: {
                        ...outlineFeature.vessel,
                        parent: outlineFeature.uuid,
                        layerGroup: LAYERS.timeVesselOutline.group,
                        drawIndex: LAYERS.timeVesselOutline.drawIndex,
                        clickIndex: LAYERS.timeVesselOutline.clickIndex,
                        layer: LAYERS.timeVesselOutline.name,
                    },
                    visible: true,
                };

                this.setState(
                    {
                        tooltips: [
                            tooltip,
                            ...tooltips.filter((x) => {
                                return x.bind;
                            }),
                        ],
                        mapProps: {
                            ...this.state.mapProps,
                            //@ts-ignore
                            viewState: {
                                ...this.state.mapProps?.viewState,
                                longitude,
                                latitude,
                                zoom: 15, // Optional: Set a desired zoom level
                                transitionDuration: 50, // Smooth transition duration
                                transitionInterpolator: new FlyToInterpolator(), // Smooth fly-to effect}
                            },
                        },
                    },
                    () => {
                        this.state.map?.setProps({ viewState: this.state.mapProps?.viewState });
                    }
                );
            });
    };

    setViewBounds = (points: [number, number][], transitionDuration = 300) => {
        if (!points?.length) return;

        if (points.length === 1) {
            const [longitude, latitude] = points[0];
            this.setState(
                {
                    mapProps: {
                        ...this.state.mapProps,
                        viewState: {
                            ...this.state.mapProps?.viewState,
                            longitude,
                            latitude,
                            zoom: 15,
                            transitionDuration,
                            transitionInterpolator: new FlyToInterpolator(),
                        },
                    },
                },
                () => {
                    this.state.map?.setProps({
                        viewState: this.state.mapProps?.viewState,
                    });
                }
            );
            return;
        }

        const canvas = this.state.mapProps?.canvas;
        if (!canvas) return;

        const width = canvas.clientWidth;
        const height = canvas.clientHeight;
        if (!width || !height) return;

        let minLng = Infinity,
            minLat = Infinity,
            maxLng = -Infinity,
            maxLat = -Infinity;

        for (const [lng, lat] of points) {
            minLng = Math.min(minLng, lng);
            minLat = Math.min(minLat, lat);
            maxLng = Math.max(maxLng, lng);
            maxLat = Math.max(maxLat, lat);
        }

        const viewport = new WebMercatorViewport({
            width,
            height,
            ...this.state.mapProps?.viewState,
        });

        const fitted = viewport.fitBounds(
            [
                [minLng, minLat],
                [maxLng, maxLat],
            ],
            {
                padding: 100,
                offset: [330, 32],
            }
        ) as unknown as {
            longitude: number;
            latitude: number;
            zoom: number;
        };
        this.setState(
            {
                mapProps: {
                    ...this.state.mapProps,
                    viewState: {
                        ...this.state.mapProps?.viewState,
                        longitude: fitted.longitude,
                        latitude: fitted.latitude,
                        zoom: fitted.zoom <= 15 ? fitted.zoom : 15,
                        transitionDuration,
                        transitionInterpolator: new FlyToInterpolator(),
                    },
                },
            },
            () => {
                this.state.map?.setProps({
                    viewState: this.state.mapProps?.viewState,
                });
            }
        );
    };
    setViewPort = (latitude: number, longitude: number, zoom?: number) => {
        this.setState(
            {
                mapProps: {
                    ...this.state.mapProps,
                    //@ts-ignore
                    viewState: {
                        ...this.state.mapProps?.viewState,
                        longitude,
                        latitude,
                        zoom: zoom || this.state.mapProps?.viewState?.zoom,
                        transitionDuration: 50, // Smooth transition duration
                        transitionInterpolator: new FlyToInterpolator(), // Smooth fly-to effect}
                    },
                },
            },
            () => {
                this.state.map?.setProps({ viewState: this.state.mapProps?.viewState });
            }
        );
    };
 

    renderOutlineAndTracks = async (): Promise<void> => {
        const { mapProps } = this.state;
        let layers = mapProps?.layers || [];

        const getFillColor = (key: string, color?: string) => {
            const defaultColor = this.props.port.meta_data.defaultColours?.[key]?.default;

            if (color) {
                return { colour: color, opacity: 0.5 };
            }

            return { colour: defaultColor, opacity: 0.5 };
        };

        const getStrokeColor = (key: string, color?: string) => {
            const defaultColor = this.props.port.meta_data.defaultColours?.[key]?.default;

            if (color) {
                return { colour: color, opacity: 1, width: 2 };
            }

            return { colour: defaultColor, opacity: 1, width: 2 };
        };

        const moveToolTipsOrders: any[] = [];

        layers = this.replaceLayers(
            layers,
            [
                /* ===========================
                 * renderOutline (UNCHANGED)
                 * =========================== */
                new GeoJsonLayer<GeoJSONFeature>({
                    id: this.props.layers.timeVesselOutline.uuid,
                    //@ts-ignore
                    data: this.state.outlineFeatures
                        .map((outlineFeature, index) => {
                            const { data, type, rawData } = findBestData(
                                outlineFeature.data,
                                null,
                                this.state.currentTime
                            );

                            if (!rawData?.data || !data) {
                                return false;
                            }

                            const { position, center } = findBestPosition(
                                data,
                                type,
                                rawData?.data,
                                //@ts-ignore
                                this.state.currentTime
                            );

                            if (!position) {
                                return false;
                            }

                            moveToolTipsOrders.push({
                                position: position,
                                layer: this.props.layers.timeVesselOutline.name,
                                parent: outlineFeature.uuid,
                            });

                            const { outline, color } = getVesselDisplayFeature(
                                position,
                                outlineFeature.vessel,
                                outlineFeature.data.offsets[position.vessel_offset_id],
                                outlineFeature.vessel.vessel_category,
                                this.props.vesselCategories,
                                this.props.port.id,
                                //@ts-ignore
                                this.state.mapProps?.viewState.zoom,
                                //@ts-ignore
                                parseFloat(this.props.port.latitude),

                                center
                            );

                            return updateFeatureProperties(
                                `vesselOutLine-${outlineFeature.vessel_id}-${index}`,
                                this.geometry.transformerGEOJSON({
                                    type: 'Feature',
                                    geometry: { type: 'Polygon', coordinates: [outline] },
                                }),
                                {
                                    index: index,
                                    time: position.valid_time_utc,
                                    dataIndex: {
                                        index: position.id,
                                        navigational_status_id: position.navigational_status_id,
                                        time: position.valid_time_utc,
                                        dataType: type,
                                    },
                                    drawIndex: this.props.layers.timeVesselOutline.drawIndex,
                                    clickIndex: this.props.layers.timeVesselOutline.clickIndex,
                                    fillColorUpdate: () => getFillColor('vesselOutlineMonoColour', color),
                                    strokeColorUpdate: () => getStrokeColor('vesselOutlineMonoColour', color),
                                    fill: getFillColor('vesselOutlineMonoColour', color),
                                    stroke: getStrokeColor('vesselOutlineMonoColour', color),
                                    name: outlineFeature.vessel.name,
                                    display: true,
                                    visible: true,
                                    layer: this.props.layers.timeVesselOutline.name,
                                    layerGroup: this.props.layers.timeVesselOutline.group,
                                    parent: outlineFeature.uuid,
                                    description: null,
                                },
                                this.props.layers.timeVesselOutline.name
                            );
                        })
                        .filter((f): f is GeoJSONFeature => f !== null),
                    //@ts-ignore
                    display: true,
                    visible: true,
                    index: this.props.layers.timeVesselOutline.drawIndex,
                    layer: this.props.layers.timeVesselOutline.name,
                    layerGroup: this.props.layers.timeVesselOutline.group,
                    pickable: true,
                    stroked: true,
                    filled: true,
                    extruded: false,
                    pointRadiusScale: 10,
                    lineWidthUnits: 'pixels',
                    getFillColor: (feature): RGBAColor => {
                        return feature.properties?.visible
                            ? [...hexToRgb(feature.properties?.fill.colour), feature.properties?.fill.opacity * 255]
                            : [0, 0, 0, 0];
                    },
                    getLineColor: (feature): RGBAColor => {
                        return feature.properties?.visible
                            ? [...hexToRgb(feature.properties?.stroke.colour), feature.properties?.stroke.opacity * 255]
                            : [0, 0, 0, 0];
                    },
                    getLineWidth: (feature) =>
                        feature.properties?.visible ? feature.properties?.stroke.width || 1 : 0,
                    getLineOpacity: (feature) => {
                        return feature.properties?.stroke ? feature.properties?.stroke.opacity || 1 : 1;
                    },
                }) as unknown as DeckGLLayer,

                /* ===========================
                 * renderTracks (UNCHANGED)
                 * =========================== */
                new GeoJsonLayer<GeoJSONFeature>({
                    id: this.props.layers.vesselTrack.uuid,
                    //@ts-ignore
                    data: this.state.trackFeatures
                        .filter((trackFeature) => {
                            return trackFeature.options.display;
                        })
                        .map((trackFeature, index) => {
                            const { data, type } = findBestData(
                                trackFeature.data,
                                this.state.startTime,
                                this.state.endTime
                            );

                            return updateFeatureProperties(
                                `trackFeature-${trackFeature.vessel_id}-${index}`,
                                this.geometry.transformerGEOJSON({
                                    type: 'Feature',
                                    geometry: {
                                        type: 'LineString',
                                        coordinates: data.map((x) => [x.east_utm, x.north_utm]),
                                    },
                                }),
                                {
                                    index: index,
                                    drawIndex: this.props.layers.vesselTrack.drawIndex,
                                    clickIndex: this.props.layers.vesselTrack.clickIndex,
                                    fillColorUpdate: () => getFillColor(`${type}Track`),
                                    strokeColorUpdate: () => getStrokeColor(`${type}Track`),
                                    fill: getFillColor(`${type}Track`),
                                    stroke: getStrokeColor(`${type}Track`),
                                    name: trackFeature.vessel.name,
                                    display: true,
                                    visible: true,
                                    layer: this.props.layers.vesselTrack.name,
                                    layerGroup: this.props.layers.vesselTrack.group,
                                    parent: trackFeature.uuid,
                                    description: null,
                                },
                                this.props.layers.vesselTrack.name
                            );
                        })
                        .filter((f): f is GeoJSONFeature => f !== null),
                    //@ts-ignore
                    display: true,
                    visible: true,
                    index: this.props.layers.vesselTrack.drawIndex,
                    layer: this.props.layers.timeVesselOutline.name,
                    layerGroup: this.props.layers.timeVesselOutline.group,
                    pickable: true,
                    stroked: true,
                    filled: true,
                    extruded: false,
                    pointRadiusScale: 2,
                    lineWidthUnits: 'pixels',
                    getFillColor: (feature): RGBAColor => {
                        return feature.properties?.visible
                            ? [...hexToRgb(feature.properties?.fill.colour), feature.properties?.fill.opacity * 255]
                            : [0, 0, 0, 0];
                    },
                    getLineColor: (feature): RGBAColor => {
                        return feature.properties?.visible
                            ? [...hexToRgb(feature.properties?.stroke.colour), feature.properties?.stroke.opacity * 255]
                            : [0, 0, 0, 0];
                    },
                    getLineWidth: (feature) =>
                        feature.properties?.visible ? feature.properties?.stroke.width || 1 : 0,
                    getLineOpacity: (feature) => {
                        return feature.properties?.stroke ? feature.properties?.stroke.opacity || 1 : 1;
                    },
                }) as unknown as DeckGLLayer,
            ],
            true
        );

        this.moveTooltipsWithTime(moveToolTipsOrders);
        await this.renderMap({ layers });
    };

    /**
     * Creates the map with background layers and a GeoJson layer.
     *
     * @returns {Promise<void>} A promise indicating the completion of map creation.
     */
    createMap = async () => {
        const layers = [
            await getLayerProvider(
                this.state.coordinate,
                this.state.backgrounds.find((x) => {
                    return x.status === 'active' && x.type === 'basemap';
                }),
                0
            ),
            await getLayerProvider(
                this.state.coordinate,
                this.state.backgrounds.find((x) => {
                    return x.status === 'active' && x.type === 'overlay';
                }),
                1
            ),
        ].filter((item) => !!item);

        const mapProps = {
            canvas: this.state.uuid,
            viewState: {
                latitude: this.state.coordinate?.[0] || 0,
                longitude: this.state.coordinate?.[1] || 0,
                zoom: this.state.zoom - 1,
                minZoom: 10, // Set the minimum zoom level
                maxZoom: 18, // Set the maximum zoom level
                pitch: 0,
                bearing: 0,
                normalize: true,
                width: this.canvasRef?.current?.clientWidth,
                height: this.canvasRef?.current?.clientHeight,
            },
            initialViewState: {
                latitude: this.state.coordinate?.[0] || 0,
                longitude: this.state.coordinate?.[1] || 0,
                zoom: this.state.zoom - 1,
                minZoom: 10, // Set the minimum zoom level
                maxZoom: 18, // Set the maximum zoom level
                pitch: 0,
                bearing: 0,
                normalize: true,
                width: this.canvasRef?.current?.clientWidth,
                height: this.canvasRef?.current?.clientHeight,
            },
            tooltip: false,
            controller: {
                doubleClickZoom: true,
            },
            getCursor: ({ isDragging }) => {
                return isDragging ? 'grabbing !important' : 'default';
            },
            onHover: this.handleOnHover,
            onDragEnd: this.handleOnDragEnd,
            onDragStart: this.handleOnDragStart,
            onDrag: this.handleOnDrag,
            onClick: this.handleOnClick,
            onViewStateChange: this.handleViewStateChange,
            onAfterRender: this.handleAfterRender,

            layers: layers,
        };

        window?.log('EVENT : Time map created', 'color: orange');
        const map = new DeckGl(mapProps);
        //@ts-ignore
        this.setState({ map, mapProps: { ...mapProps, canvas: map.getCanvas() } }, async () => {
            this.props.setMapAction({ time: map });
            this.props.setMapControlsAction({
                time: {
                    hideTooltips: this.hideTooltips,
                    clearTooltips: this.clearTooltips,
                    getDrawingCount: this.getDrawingCount,
                    hideTooltipById: this.hideTooltipById,
                    flagTooltipId: this.flagTooltipId,
                    removeDrawingById: this.removeDrawingById,
                    removeDrawingToolTipById: this.removeDrawingToolTipById,
                    updateTracks: this.updateTracks,
                    refreshFeatures: this.refreshFeatures,
                    clearMap: this.clearMap,
                    goToVesselLocation: this.goToVesselLocation,
                    portAreaFeatureVisiblity: this.portAreaFeatureVisiblity,
                    setDrawingMode: this.setDrawingMode,
                    getDrawingMode: this.getDrawingMode,
                    getDrawingFeatures: this.getDrawingFeatures,
                    zoomIn: this.zoomIn,
                    zoomOut: this.zoomOut,
                    getCurrentTime: this.getCurrentTime,
                    closeTimeMode: this.closeTimeMode,
                    setViewPort: this.setViewPort,
                    setViewBounds: this.setViewBounds,
                    jumpToTime: this.jumpToTime,
                },
            });

            this.createPortAreas(this.state.pilotagePlans.length >= 1);
        });
    };

    getCurrentTime = () => {
        return this.state.currentTime;
    };

    //unused function
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    afterClick = (id: number, time: DateTimeFormat | null) => {};

    changeSpeed = (speed: number) => {
        this.setState({ speed });
    };

    jumpToTime: JumpToTimeInterface = (selectedTime) => {
        this.setState(
            {
                currentTime: selectedTime,
            },
            this.renderOutlineAndTracks
        );
    };

    changeTime = (seconds: number) => {
        this.setState(
            (prevState) => {
                const newTime = prevState.currentTime?.clone().plus({ second: seconds });

                if (!newTime) {
                    return null;
                } // Safeguard in case `currentTime` is undefined

                if (newTime.isAfter(prevState.endTime?.date)) {
                    return { currentTime: prevState?.endTime?.clone() };
                }

                if (newTime.isBefore(prevState.startTime?.date)) {
                    return { currentTime: prevState?.startTime?.clone() };
                }

                return { currentTime: newTime };
            },
            () => {
                //@ts-ignore
                this.props.menuControls?.time?.setSelectedTime(this.state.currentTime);
                this.renderOutlineAndTracks();
            }
        );
    };

    render() {
        return (
            <div
                className={twclsx(
                    !this.state.active && 'hidden',
                    'h-page',
                    'padding-transition',
                    'fixed',
                    'z-10',
                    'left-0',
                    'right-0',
                    'margin-transition',
                    'ml-14',
                    'w-fullPage',
                    'mt-14'
                )}
            >
                <canvas
                    id={this.state.uuid}
                    ref={this.canvasRef}
                    className={twclsx(
                        'fixed',
                        this.state.drawingMode !== 'pointer' && 'mouse-crosshair',
                        'z-10',
                        'w-screen',
                        'h-screen',
                        'left-0',
                        'right-0'
                    )}
                ></canvas>
                <ToolTip
                    mode="time"
                    updateTooltips={this.updateTooltips}
                    updateDrawinglabel={this.updateDrawinglabel}
                    currentTime={this.state.currentTime}
                    tooltips={this.state.tooltips}
                ></ToolTip>
                <ChangeLayer
                    save={true}
                    backgrounds={this.state.backgrounds}
                    changeBackground={this.changeBackground}
                    changeOverlay={this.changeOverlay}
                    move={true}
                ></ChangeLayer>
                <MeasurePosition
                    hoverMeasureData={this.state.hoverMeasureData}
                    coordinate={this.state?.coordinate}
                ></MeasurePosition>
                <MousePosition backgrounds={this.state.backgrounds} coordinate={this.state?.coordinate}></MousePosition>
                <Attribution backgrounds={this.state.backgrounds}></Attribution>
                <Controls parent="time" move={true}></Controls>
                <TimeControl
                    backgrounds={this.state.backgrounds}
                    speed={this.state.speed}
                    changeSpeed={this.changeSpeed}
                    changeTime={this.changeTime}
                    startTimer={this.startTimer}
                    stopTimer={this.stopTimer}
                    currentTime={this.state.currentTime}
                    startTime={this.state.startTime}
                    endTime={this.state.endTime}
                    play={this.state.play}
                ></TimeControl>
            </div>
        );
    }
}

const timeStateToProps = (state: RootState) => ({
    layout: state.layout.layout,
    vesselCategories: state.port.vesselCategories,
    port: state.port.port,
    layers: state.layout.layers,
    outlineInterval: state.map.outlineInterval,
    switchs: state.layout.switchs,
    pilotagePlanSettings: state.layout.pilotagePlanSettings,
    pilotagePlans: state.port.pilotagePlans,
    mainControls: state.layout.mainControls,
    dateTimeFormat: state.port.dateTimeFormat,
    menuControls: state.layout.menuControls,
    openModelFunction: state.layout.openModelFunction,
});
const timeDispatchToProps = (dispatch: Dispatch) => ({
    setMapAction: bindActionCreators(setMapAction, dispatch),
    setMapControlsAction: bindActionCreators(setMapControlsAction, dispatch),
    setErrorHandlerAction: bindActionCreators(setErrorHandlerAction, dispatch),
    setSystemLoadingAction: bindActionCreators(setSystemLoadingAction, dispatch),
    showMessageAction: bindActionCreators(showMessageAction, dispatch),
    setLayoutAction: bindActionCreators(setLayoutAction, dispatch),
});

export default connect(timeStateToProps, timeDispatchToProps)(Time);
